import React, { useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { platformAPI } from '../../services/platformAPI';
import WorkspaceModal from './WorkspaceModal';

export const revisionColumn = {
  key: 'revision',
  label: 'Rev.',
  width: '72px',
  render: (value) => value || 1
};

const whenLabel = (value, actorName) => {
  if (!value || actorName === 'Already on file') return 'Already on file';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Already on file';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function useMasterRevision({ entity, catalog = '' }) {
  const [prompt, setPrompt] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');

  const askReason = (current) => new Promise((resolve) => {
    setReason('');
    setReasonError('');
    setPrompt({
      resolve,
      current: Number(current) || 1
    });
  });

  const closeReason = (value) => {
    const resolve = prompt?.resolve;
    setPrompt(null);
    setReason('');
    setReasonError('');
    if (resolve) resolve(value || '');
  };

  const submitReason = () => {
    const text = reason.trim();
    if (!text) {
      setReasonError('Enter the reason for this modification.');
      return;
    }
    closeReason(text);
  };

  const open = async (row) => {
    const id = row?._id || row?.id;
    if (!id) return;
    setHistoryError('');
    setHistory({
      label: row.name || row.label || row.designation || row.title || 'Record',
      current: row.revision || 1,
      revisions: [],
      loading: true
    });
    try {
      const response = await platformAPI.listMasterRevisions({ entity, id, catalog });
      const data = response.data?.data || {};
      setHistory({
        label: row.name || row.label || row.designation || row.title || 'Record',
        current: data.current || row.revision || 1,
        revisions: data.revisions || [],
        loading: false
      });
    } catch (error) {
      setHistory((current) => ({ ...(current || {}), loading: false }));
      setHistoryError(error.response?.data?.message || 'Revision history could not be loaded.');
    }
  };

  const wrapActions = (builder) => (row) => {
    const actions = (typeof builder === 'function' ? builder(row) : builder) || [];
    const item = { type: 'revision', label: 'Revision info', onClick: () => open(row) };
    const deleteAt = actions.findIndex((action) => action.type === 'delete');
    if (deleteAt >= 0) {
      const next = [...actions];
      next.splice(deleteAt, 0, item);
      return next;
    }
    return [...actions, item];
  };

  const modals = (
    <>
      <WorkspaceModal
        show={Boolean(prompt)}
        onHide={() => closeReason('')}
        title="Reason for this change"
        description={prompt ? `This record is at revision ${prompt.current}. Saving makes it revision ${prompt.current + 1}.` : ''}
        size="sm"
        className="workspace-modal--confirm"
        enableTestFillHotkey={false}
        footer={(
          <>
            <Button variant="outline-secondary" size="sm" onClick={() => closeReason('')}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submitReason}>Save change</Button>
          </>
        )}
      >
        <Form.Group>
          <Form.Label>Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this master being changed?"
            autoFocus
          />
          {reasonError ? <div className="text-danger small mt-2">{reasonError}</div> : null}
        </Form.Group>
      </WorkspaceModal>
      <WorkspaceModal
        show={Boolean(history)}
        onHide={() => { setHistory(null); setHistoryError(''); }}
        title="Revision history"
        description={history ? `${history.label}. Current revision ${history.current || 1}.` : ''}
        size="lg"
        enableTestFillHotkey={false}
        footer={(
          <Button variant="outline-secondary" size="sm" onClick={() => { setHistory(null); setHistoryError(''); }}>Close</Button>
        )}
      >
        {historyError ? <div className="text-danger small mb-2">{historyError}</div> : null}
        {history?.loading ? <p className="mb-0">Loading revision history…</p> : (
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Rev.</th>
                <th>When</th>
                <th>Changed by</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {(history?.revisions || []).map((row) => (
                <tr key={row.revision}>
                  <td>{row.revision}</td>
                  <td>{whenLabel(row.createdAt, row.actorName)}</td>
                  <td>{row.actorName || 'User'}</td>
                  <td>{row.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </WorkspaceModal>
    </>
  );

  return { askReason, open, wrapActions, modals };
}
